import { useState } from 'react';
import { PlusIcon, UserCheckIcon } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { useCurrentBusiness } from '@/hooks/use-current-business';
import {
  createEmployee,
  employeesQueryKey,
  listEmployees,
  updateEmployee,
  type CreateEmployeeRequest,
  type UpdateEmployeeRequest,
} from '@/lib/api/employees';

import { AddEmployeeModal } from './add-employee-modal';
import { EmployeeDetailModal } from './employee-detail-modal';
import { EmployeesGrid } from './employees-grid';
import type { StatusFilter } from './types';

export function Employees() {
  const { t } = useLanguage();
  const business = useCurrentBusiness();
  const businessId = business?.business_id;

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [isAdding, setIsAdding] = useState(false);
  const [activeEmployeeId, setActiveEmployeeId] = useState<string | null>(null);

  // ── Data ──────────────────────────────────────────────────────────────────────

  const employeesQuery = useQuery({
    queryKey: employeesQueryKey(businessId ?? '', statusFilter),
    queryFn: () =>
      listEmployees(businessId!, statusFilter === 'all' ? undefined : statusFilter),
    enabled: Boolean(businessId),
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateEmployeeRequest) => createEmployee(businessId!, body),
    meta: { successMessage: t.employeeAdded },
    onSuccess: () => setIsAdding(false),
  });

  const updateMutation = useMutation({
    mutationFn: (body: UpdateEmployeeRequest) =>
      updateEmployee(businessId!, activeEmployeeId!, body),
    meta: { successMessage: t.employeeUpdated },
    onSuccess: () => setActiveEmployeeId(null),
  });

  const employees = employeesQuery.data ?? [];
  const activeCount = employees.filter((e) => e.status === 'active').length;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <UserCheckIcon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold leading-tight">{t.employees}</h1>
            <p className="text-xs text-muted-foreground">
              {activeCount} {t.employeeActive.toLowerCase()} · {employees.length}{' '}
              {t.allEmployees.toLowerCase()}
            </p>
          </div>
        </div>
        <Button
          size="sm"
          className="gap-1.5"
          data-testid="add-employee"
          onClick={() => setIsAdding(true)}
        >
          <PlusIcon className="w-4 h-4" />
          {t.addEmployee}
        </Button>
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-1 mb-5 border-b">
        {(['all', 'active', 'inactive'] as const).map((s) => {
          const label =
            s === 'all'
              ? t.allStatuses
              : s === 'active'
                ? t.activeEmployees
                : t.inactiveEmployees;

          return (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={[
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                statusFilter === s
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              ].join(' ')}
              data-testid={`filter-status-${s}`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {employeesQuery.isPending ? (
        <p className="text-sm text-muted-foreground py-10 text-center">{t.loading}</p>
      ) : (
        <EmployeesGrid employees={employees} onSelect={setActiveEmployeeId} />
      )}

      {isAdding && (
        <AddEmployeeModal
          onClose={() => setIsAdding(false)}
          onSave={(body) => createMutation.mutate(body)}
          isSaving={createMutation.isPending}
        />
      )}

      {activeEmployeeId && businessId && (
        <EmployeeDetailModal
          businessId={businessId}
          employeeId={activeEmployeeId}
          onClose={() => setActiveEmployeeId(null)}
          onSave={(body) => updateMutation.mutate(body)}
          isSaving={updateMutation.isPending}
        />
      )}
    </div>
  );
}
